'use client';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {forwardRef} from 'react';
import {GalleryTile} from './GalleryTile';
import type {MediaAsset} from './types';

export interface GalleryCarouselProps {
  assets: MediaAsset[];
  onOpen?: (index: number) => void;
}

// Figma "Assets" (1:10526): 16px gap, three equal tiles per row on desktop.
const TILES_PER_VIEW_DESKTOP = 3;
const TILE_WIDTH_MOBILE = 'min(340px, 85vw)';

const Track = styled(Box, {shouldForwardProp: propName => propName !== 'oDesktopOverflows'})<{oDesktopOverflows: boolean}>(
  ({theme, oDesktopOverflows}) => ({
    minWidth: 0,
    display: 'flex',
    gap: theme.spacing(2),
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    scrollSnapType: 'x mandatory',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {display: 'none'},
    '& > *': {
      flex: '0 0 auto',
      scrollSnapAlign: 'start',
      width: TILE_WIDTH_MOBILE,
      [theme.breakpoints.up('md')]: {
        width: `calc((100% - ${theme.spacing(4)}) / ${TILES_PER_VIEW_DESKTOP})`,
      },
    },
    // Width must mirror the tile width + gap above, or the last tile's snap-start
    // becomes unreachable again and it shares the view with its neighbours.
    '& > [data-carousel-end-spacer]': {
      scrollSnapAlign: 'none',
      width: `calc(100% - ${TILE_WIDTH_MOBILE} - ${theme.spacing(2)})`,
      [theme.breakpoints.up('md')]: {
        display: oDesktopOverflows ? 'block' : 'none',
        width: `calc(100% - (100% - ${theme.spacing(4)}) / ${TILES_PER_VIEW_DESKTOP} - ${theme.spacing(2)})`,
      },
    },
  }),
);

export const GalleryCarousel = forwardRef<HTMLDivElement, GalleryCarouselProps>(function GalleryCarousel({assets, onOpen}, ref) {
  if (assets.length === 0) {
    return null;
  }

  return (
    <Track ref={ref} oDesktopOverflows={assets.length > TILES_PER_VIEW_DESKTOP}>
      {assets.map((asset, index) => (
        <GalleryTile
          key={`${asset.src}-${index}`}
          asset={asset}
          onOpen={() => {
            onOpen?.(index);
          }}
        />
      ))}
      {assets.length > 1 && <Box data-carousel-end-spacer aria-hidden />}
    </Track>
  );
});

GalleryCarousel.displayName = 'GalleryCarousel';
